/**
 * Shared dex-variant visual language.
 *
 * One source of truth for every dex variant's short label, colour token, and
 * unified glyph ("logo"), plus the two primitives that render them:
 *   - <VariantBadge>    — the coloured icon+label chip shown on trade lines.
 *   - <CenteredPillRow> — a horizontal pill selector that centres the pill you
 *     pick, and rests flush left when it cannot — see centreActive.
 *
 * Colours reuse the variant tokens in index.css (--shiny, --lucky, …) so every
 * surface that shows a variant reads in the same colour language. Variants with
 * no dedicated token (shadow / mega / gmax / shundo / shinyLucky) carry a
 * literal hsl triple instead, matching the PoGo page-colour families in
 * pogoPokedexVariantClassifier (COLOUR_FAMILY_ALLOWED_VARIANTS).
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import type { LucideIcon } from "lucide-react";

import { getPageScrollTop, scrollPageTo } from "@/lib/pageScroll";
import {
  CircleDot, Sparkles, Clover, Star, ChevronsDown, ChevronsUp,
  Gem, Maximize2, Flame, Droplet, Award, Gift, CheckCheck, Atom,
  Globe2, Shirt,
} from "lucide-react";

export type DexVariantKey =
  | "pokemon" | "shiny" | "lucky" | "perfect" | "xxs" | "xxl"
  | "mega" | "gmax" | "shadow" | "purified" | "complete"
  | "shundo" | "shinyLucky" | "dynamax"
  | "regional" | "costume";

export interface VariantVisual {
  /** Short label used on pills + badges. */
  label: string;
  /** hsl() body — a CSS var for tokenised variants, a literal triple otherwise. */
  token: string;
  /** Unified glyph for the variant (its "logo"). */
  Icon: LucideIcon;
}

export const DEX_VARIANT_META: Record<DexVariantKey, VariantVisual> = {
  pokemon:    { label: "Main",     token: "var(--primary)",  Icon: CircleDot },
  shiny:      { label: "Shiny",    token: "var(--shiny)",    Icon: Sparkles },
  lucky:      { label: "Lucky",    token: "var(--lucky)",    Icon: Clover },
  perfect:    { label: "100%",     token: "var(--perfect)",  Icon: Star },
  xxs:        { label: "XXS",      token: "var(--xxs)",      Icon: ChevronsDown },
  xxl:        { label: "XXL",      token: "var(--xxl)",      Icon: ChevronsUp },
  mega:       { label: "Mega",     token: "330 72% 58%",     Icon: Gem },
  gmax:       { label: "G-Max",    token: "312 68% 56%",     Icon: Maximize2 },
  dynamax:    { label: "D-MAX",    token: "var(--dynamax)",  Icon: Atom },
  shadow:     { label: "Shadow",   token: "280 70% 62%",     Icon: Flame },
  purified:   { label: "Purified", token: "var(--purified)", Icon: Droplet },
  complete:   { label: "Complete", token: "var(--primary)",  Icon: CheckCheck },
  shundo:     { label: "Shundo",      token: "286 85% 68%",  Icon: Award },
  shinyLucky: { label: "Shiny Lucky", token: "50 92% 52%",   Icon: Gift },
  // form-dexes: a globe for the regional forms, a shirt for the costumes.
  regional:   { label: "Regional",    token: "199 80% 55%",  Icon: Globe2 },
  costume:    { label: "Costume",     token: "22 88% 58%",   Icon: Shirt },
};

/** Solid hsl() for a variant. */
export const variantColor = (k: DexVariantKey): string => `hsl(${DEX_VARIANT_META[k].token})`;
/** Alpha hsl() for a variant. */
export const variantAlpha = (k: DexVariantKey, a: number): string => `hsl(${DEX_VARIANT_META[k].token} / ${a})`;
/** Type guard for coercing a raw variant string to a known key. */
export function isDexVariantKey(v: string): v is DexVariantKey {
  return Object.prototype.hasOwnProperty.call(DEX_VARIANT_META, v);
}

/* ────────────────────────────────────────────────────────────────────────────
   Server-driven dex RENAME overrides (admin dex-config editor).
   DEX_VARIANT_META holds the bundled default labels; an admin can rename a dex
   from the portal and the dex-config syncer feeds the override here.
   getVariantLabel() resolves the override first, then the bundled label — every
   dex-name surface routed through it (tab bar, badges, compare, widgets) picks
   the rename up app-wide with no rebuild.
   ──────────────────────────────────────────────────────────────────────────── */
const runtimeLabelOverrides: Record<string, string> = {};

/** Replace the runtime label overrides. Empty/blank values are ignored; passing
 *  an empty map clears them (bundled labels show through). */
export function setRuntimeVariantLabels(labels: Record<string, string>): void {
  for (const k of Object.keys(runtimeLabelOverrides)) delete runtimeLabelOverrides[k];
  for (const [k, v] of Object.entries(labels ?? {})) {
    if (typeof v === "string" && v.trim().length > 0) runtimeLabelOverrides[k] = v.trim();
  }
}

/** The effective display label for a variant: admin override, else bundled. */
export function getVariantLabel(variant: string): string {
  return (
    runtimeLabelOverrides[variant] ??
    (isDexVariantKey(variant) ? DEX_VARIANT_META[variant].label : variant)
  );
}

/**
 * Coloured icon + short label chip. Shown on each trade line so it's obvious
 * which dex the line is surfacing (e.g. a base Main copy vs an XXL copy).
 */
export function VariantBadge({ variant, className }: { variant: DexVariantKey; className?: string }) {
  const meta = DEX_VARIANT_META[variant];
  const Icon = meta.Icon;
  const label = getVariantLabel(variant);
  return (
    <span
      className={`shrink-0 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${className ?? ""}`}
      style={{
        background: variantAlpha(variant, 0.16),
        border: `1px solid ${variantAlpha(variant, 0.45)}`,
        color: variantColor(variant),
        whiteSpace: "nowrap",
      }}
      data-testid={`variant-badge-${variant}`}
      title={`${label} dex`}
    >
      <Icon className="w-2.5 h-2.5" aria-hidden />
      {label}
    </span>
  );
}

export interface CenteredPillItem {
  key: string;
  label: string;
  /**
   * Complete CSS colour (not an hsl() body) — callers wrap their own token, so
   * hsl-triple families (`hsl(var(--shiny))`) and whole-colour families
   * (`var(--event-color-raid)`) both work. When present the active pill fills
   * with it and idle pills tint their border; omit it for a neutral pill.
   */
  color?: string;
  Icon?: LucideIcon | null;
}

/**
 * Horizontal pill selector. Controlled: the parent owns the active key.
 *
 * The pill you pick locks to the CENTRE of the row (see centreActive), and the
 * row holds its place on screen while the page behind it changes length (see
 * the anchor in `pick`).
 */
export function CenteredPillRow({
  items,
  activeKey,
  onSelect,
  testId,
  pillTestIdPrefix,
  scrollerRef,
  ariaLabel,
  size = "md",
  className,
  style,
}: {
  items: readonly CenteredPillItem[];
  activeKey: string;
  onSelect: (key: string) => void;
  testId?: string;
  /** Prefix for each pill's testid (`${prefix}-${key}`); defaults to `${testId}`. */
  pillTestIdPrefix?: string;
  /** Shared handle to the scroller node (e.g. for a slim-bar measurement). */
  scrollerRef?: { current: HTMLElement | null };
  ariaLabel?: string;
  /** "xs" is the letter row's size: 27 pills a single flick can cross. */
  size?: "xs" | "sm" | "md";
  className?: string;
  style?: CSSProperties;
}) {
  const localRef = useRef<HTMLDivElement | null>(null);
  const setScroller = (node: HTMLDivElement | null) => {
    localRef.current = node;
    if (scrollerRef) scrollerRef.current = node;
  };

  /**
   * Lock the active pill to the centre of the row.
   *
   * CENTRING, BUT CLAMPED — that is the whole design. The first version centred
   * by reserving `paddingInline: 50%`, which let even the first pill reach the
   * middle; since the default selection IS the first pill on every row that uses
   * this ("Main", "All"), the band opened with an empty left half and the rest
   * of the pills marching off the right edge. The version after it dropped
   * centring altogether and only revealed a pill that was off-screen, which lost
   * the thing the row is for: after a tap you cannot tell at a glance which pill
   * is live without hunting along the row.
   *
   * Clamping to the scrollable range gives both. A pill in the middle of a long
   * row centres exactly; the first and last pills can only reach the ends, so
   * the row still rests flush left, lined up with the content beneath it, and
   * never leaves a strip of empty track at either edge.
   */
  const centreActive = (behavior: ScrollBehavior) => {
    const scroller = localRef.current;
    if (!scroller) return;
    const pill = scroller.querySelector<HTMLElement>(`[data-pill-key="${CSS.escape(activeKey)}"]`);
    if (!pill) return;

    const centre = pill.offsetLeft + pill.offsetWidth / 2 - scroller.clientWidth / 2;
    const furthest = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const target = Math.round(Math.min(Math.max(centre, 0), furthest));
    if (Math.abs(target - scroller.scrollLeft) < 1) return;

    // Horizontal-only: never touch vertical scroll (scrollIntoView can).
    // scrollTo is absent in jsdom; fall back to setting scrollLeft directly.
    if (typeof scroller.scrollTo === "function") {
      scroller.scrollTo({ left: target, behavior });
    } else {
      scroller.scrollLeft = target;
    }
  };

  useEffect(() => {
    centreActive("smooth");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, items.length]);

  /**
   * Where the row sat on screen when the pill was tapped, so it can be put back
   * there afterwards.
   *
   * FILTERING CHANGES THE PAGE'S LENGTH. Pick a category with two events and the
   * list below collapses by thousands of pixels; the document becomes shorter
   * than the current scroll position, the browser clamps the scroll, and this
   * pinned row un-pins and drops back to its resting place far down the page —
   * so the control you just used walks off under your finger and the results you
   * asked for are somewhere above. Reported on the Calendar (Go Fest),
   * 2026-08-12.
   *
   * The row is put back at the same viewport y after the new content commits. If
   * the page is now too short to allow that the browser clamps again and the row
   * lands as high as it can, which is the best that exists.
   */
  const anchorTop = useRef<number | null>(null);
  // Counts TAPS, not selections. Keying the correction on `activeKey` would
  // leave a stale anchor behind whenever a tap did not change the selection
  // (tapping the live pill), and spend it on some later change — scrolling the
  // page to where the row sat minutes ago. A tick always re-renders, so the
  // effect always runs exactly once per tap, and a selection that arrives some
  // other way (a deep link, a reset) never moves the page at all.
  const [pickTick, setPickTick] = useState(0);

  const pick = (key: string) => {
    anchorTop.current = localRef.current?.getBoundingClientRect().top ?? null;
    setPickTick((n) => n + 1);
    onSelect(key);
  };

  useLayoutEffect(() => {
    const want = anchorTop.current;
    anchorTop.current = null;
    const scroller = localRef.current;
    if (want == null || !scroller) return;
    const drift = scroller.getBoundingClientRect().top - want;
    // Sub-pixel drift is layout noise, not a jump worth scrolling for.
    if (Math.abs(drift) < 1) return;
    scrollPageTo(getPageScrollTop() + drift);
  }, [pickTick]);

  const pad = size === "xs"
    ? { px: "px-2", py: "py-0.5", text: "text-[10.5px]", icon: "w-2.5 h-2.5" }
    : size === "sm"
    ? { px: "px-3", py: "py-1.5", text: "text-[11px]", icon: "w-2.5 h-2.5" }
    : { px: "px-3.5", py: "py-2", text: "text-[12.5px]", icon: "w-3 h-3" };

  const pillPrefix = pillTestIdPrefix ?? testId;

  return (
    <div
      ref={setScroller}
      data-testid={testId}
      role="tablist"
      aria-label={ariaLabel}
      className={`relative flex gap-1.5 overflow-x-auto scrollbar-hide no-scrollbar snap-x scroll-smooth ${className ?? ""}`}
      /**
       * No inline padding of its own — it used to be 50% (the picker-wheel
       * centring). A pinned row gets its gutter from `.sticky-band-bleed`, which
       * knows the container's real inset; adding a second one here would push
       * the first pill off the content-left the row is supposed to align to.
       * Centring is done by scrolling instead (centreActive), which is what
       * keeps the ends flush.
       *
       * Snapping stays PROXIMITY (no `snap-mandatory`): a nudge along the row
       * should be able to stop between pills. Where it does snap, it snaps to a
       * pill's centre, so a settle after a flick agrees with where a tap puts
       * things rather than undoing it.
       */
      style={{ scrollPaddingInline: 12, ...style }}
    >
      {items.map((it) => {
        const active = it.key === activeKey;
        const solid = it.color ?? "var(--accent-fill)";
        const idleBorder = it.color
          ? `color-mix(in srgb, ${it.color} 40%, transparent)`
          : "var(--accent-fill-glow)";
        const Icon = it.Icon;
        return (
          <button
            key={it.key}
            type="button"
            role="tab"
            aria-selected={active}
            data-pill-key={it.key}
            data-selected={active || undefined}
            data-testid={pillPrefix ? `${pillPrefix}-${it.key}` : undefined}
            onClick={() => pick(it.key)}
            className={`shrink-0 snap-center inline-flex items-center justify-center gap-1 rounded-full font-semibold transition ${pad.px} ${pad.py} ${pad.text}`}
            style={
              active
                ? {
                    background: solid,
                    // --text-on-accent is tuned for the ACCENT fill only —
                    // under Instinct light it is a dark brown ink, which was
                    // unreadable on a variant-coloured fill (the red D-Max
                    // pill). Variant fills are all mid-dark saturated hues,
                    // so they take fixed white ink regardless of accent.
                    color: it.color ? "#fff" : "var(--text-on-accent)",
                    border: `1px solid ${solid}`,
                  }
                : { background: "var(--surface-dark-7)", color: "var(--text-ink-faint)", border: `1px solid ${idleBorder}` }
            }
          >
            {Icon && <Icon className={pad.icon} aria-hidden />}
            {it.label}
          </button>
        );
      })}
    </div>
  );
}
